import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import BottomNav from "@/components/app/BottomNav";
import EnableNotificationsNudge from "@/components/app/EnableNotificationsNudge";
import { CLUBS } from "@/data/clubs";
import { Heart } from "lucide-react";

const initialsOf = (name) => (name || "?").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();
const clubColor = (n) => CLUBS.find(c => c.name === n)?.color || "#152A55";
const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), n = new Date();
  const s = Math.max(0, (n - d) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s/60)}m`;
  if (s < 86400) return `${Math.floor(s/3600)}h`;
  return `${Math.floor(s/86400)}d`;
};

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches");
        setMatches(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const withMessages = matches.filter(m => m.last_message);
  const newMatches = matches.filter(m => !m.last_message);

  return (
    <div className="min-h-screen bg-footy-bg text-white pb-24 grain">
      <header className="sticky top-0 z-30 bg-footy-bg/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-md mx-auto px-5 py-4">
          <h1 className="font-display text-3xl uppercase tracking-wider">Matches</h1>
        </div>
      </header>

      <div className="max-w-md mx-auto px-5 pt-6" data-testid="matches-list">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 rounded-2xl bg-white/5 animate-pulse" />)}</div>
        ) : (
          <>
            <EnableNotificationsNudge source="matches" />
            {matches.length === 0 ? (
              <div className="footy-card p-8 text-center">
                <Heart className="mx-auto text-footy-red mb-3" />
                <h3 className="font-display text-2xl uppercase">No matches yet</h3>
                <p className="text-slate-400 mt-2 text-sm">Head back to Discover and start swiping.</p>
                <Link to="/discover" className="footy-btn-primary mt-6 inline-flex" data-testid="matches-empty-cta">Go discover</Link>
              </div>
            ) : (
              <>
                {newMatches.length > 0 && (
              <>
                <p className="footy-label mt-2">New matches</p>
                <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
                  {newMatches.map(m => (
                    <Link key={m.id} to={`/chat/${m.id}`} className="flex-none text-center" data-testid={`new-match-${m.id}`}>
                      <Avatar name={m.other.name} photo={m.other.photo_url} club={m.other.club} size={72} />
                      <div className="mt-2 text-xs font-semibold truncate w-20">{m.other.name}</div>
                    </Link>
                  ))}
                </div>
              </>
            )}
            {withMessages.length > 0 && (
              <>
                <p className="footy-label mt-6">Messages</p>
                <div className="space-y-2">
                  {withMessages.map(m => (
                    <Link key={m.id} to={`/chat/${m.id}`}
                      className="flex items-center gap-3 p-3 footy-card hover:bg-footy-cardHover transition-colors"
                      data-testid={`match-thread-${m.id}`}>
                      <Avatar name={m.other.name} photo={m.other.photo_url} club={m.other.club} size={52} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-bold truncate">{m.other.name}</span>
                          <span className="text-xs text-slate-400 flex-none">{timeAgo(m.last_at)}</span>
                        </div>
                        <div className="text-sm text-slate-400 truncate">{m.last_message}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
              </>
            )}
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
}

function Avatar({ name, photo, club, size = 56 }) {
  const color = clubColor(club);
  return (
    <div style={{ width: size, height: size }}
      className="relative rounded-full overflow-hidden border-2 flex-none"
      >
      <div className="absolute inset-0 rounded-full border-2" style={{ borderColor: color }} />
      {photo ? (
        <img src={photo} alt="" className="w-full h-full object-cover rounded-full" />
      ) : (
        <div className="w-full h-full rounded-full flex items-center justify-center font-display text-2xl text-white"
             style={{ background: `linear-gradient(160deg, ${color}, #061022)` }}>
          {initialsOf(name)}
        </div>
      )}
    </div>
  );
}
